import { useRef, useEffect, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, AdaptiveDpr } from "@react-three/drei";
import * as THREE from "three";
import { Suspense } from "react";
import { Warehouse } from "./Warehouse";
import { xrayUniforms } from "./xray";
import { AlertBeamLayer } from "./AlertBeamLayer";
import { SensorLayer } from "./SensorLayer";
import { HeatmapLayer } from "./HeatmapLayer";
import { SpaghettiLayer } from "./SpaghettiLayer";
import { SimulationRoutesLayer } from "./SimulationRoutesLayer";
import { OccupancyLayerContainer } from "./OccupancyLayerContainer";
import { RackVolumesLayerContainer } from "./RackVolumesLayerContainer";
import { KpiBadgeLayerContainer } from "./KpiBadgeLayerContainer";
import { boundsStable, contentBounds, fitPose } from "./sceneFit";
import { WalkControls, type OrbitPose } from "./WalkControls";
import { useBlockerField } from "./useBlockerField";
import { NDC_CORNERS, publishViewRays, clearViewRays } from "./viewFootprint";
import { useUIStore } from "../stores/uiStore";
import { useTelemetryStore } from "../stores/telemetryStore";
import { useSiteStore } from "../stores/siteStore";
import { useSiteId } from "../hooks/useSiteId";
import { tokens } from "../styles/tokens";
import zonesJson from "../../../infra/seed/zones.json";

// Minimal interface — avoids importing OrbitControlsImpl across three.js patch versions.
interface IControls {
  target: THREE.Vector3;
  maxDistance?: number;
  update(): void;
}

const _headlightDir = new THREE.Vector3();
/**
 * ADR 210: a fill light pinned to the camera and aimed along the view
 * direction, so the face you are looking at is always lit — orbit however you
 * like, the side toward you can never be the dark side. Moderate intensity by
 * design: it only lifts the shadowed side to readable; the fixed key light
 * (Warehouse.tsx) still does the shading, so the scene keeps real form instead
 * of the flat, frontal-flash look a strong headlight (or ADR 209) would give.
 * World-space — must sit OUTSIDE the warehouse's editable transform group.
 */
function CameraHeadlight({ intensity }: { intensity: number }) {
  const ref = useRef<THREE.DirectionalLight>(null);
  const camera = useThree((s) => s.camera);
  useFrame(() => {
    const light = ref.current;
    if (!light) return;
    light.position.copy(camera.position);
    camera.getWorldDirection(_headlightDir);
    // Target one unit ahead along the view ray; updateMatrixWorld so the light
    // direction is fresh this frame (the built-in target is not in the scene).
    light.target.position.copy(camera.position).add(_headlightDir);
    light.target.updateMatrixWorld();
  });
  return <directionalLight ref={ref} intensity={intensity} />;
}

/**
 * Apply a fitted pose (ADR 204). The zoom-out ceiling must be raised ON THE
 * CONTROLS before update() — the React state path lands a render later, and
 * update() clamps the camera to the OLD ceiling first (the fit then silently
 * stops at 200 m and crops the near corner, which was the original complaint).
 */
function applyPose(
  camera: THREE.Camera, ctrl: IControls,
  pose: { position: THREE.Vector3; target: THREE.Vector3 },
  onFitDistance: (d: number) => void,
) {
  const dist = pose.position.distanceTo(pose.target);
  if (ctrl.maxDistance !== undefined) ctrl.maxDistance = Math.max(ctrl.maxDistance, Math.ceil(dist * 2));
  camera.position.copy(pose.position);
  ctrl.target.copy(pose.target);
  ctrl.update();
  onFitDistance(dist);
}

// Demo-frame fallbacks, used only while the scene has no measurable content
// (nothing uploaded yet). Everything real goes through sceneFit (ADR 204).
const cameraPositions: Record<string, [number, number, number]> = {
  iso:   [70, 60, 70],
  top:   [40, 90, 30],
  aisle: [30, 6,  30],
  dock:  [10, 12, 5],
};

const CX = ((zonesJson.widthM  ?? 60) / 2);
const CZ = ((zonesJson.heightM ?? 40) / 2);

// ---------------------------------------------------------------------------
// Camera preset — jumps to the named pose when the preset changes, derived
// from the CONTENT bounds at click time (ADR 204): "Corredor"/"Docas" used to
// aim at demo coordinates, so on any real warehouse they framed empty floor.
// ---------------------------------------------------------------------------

function PresetCamera({
  preset,
  controlsRef,
  onFitDistance,
}: {
  preset: string;
  controlsRef: React.RefObject<IControls | null>;
  onFitDistance: (d: number) => void;
}) {
  const { camera, scene, size } = useThree();
  const prevPreset = useRef<string | null>(null);

  useEffect(() => {
    if (prevPreset.current === preset) return;
    const isFirst = prevPreset.current === null;
    prevPreset.current = preset;
    // Initial mount: leave the camera where the Canvas put it — FitOnLoad owns
    // the first framing once the async content (GLB, occupancy) has settled.
    if (isFirst) return;
    const ctrl = controlsRef.current;
    if (!ctrl) return;
    const box = contentBounds(scene);
    if (box) {
      applyPose(camera, ctrl, fitPose(box, preset, size.width / size.height), onFitDistance);
    } else {
      const [px, py, pz] = cameraPositions[preset] ?? cameraPositions.iso;
      camera.position.set(px, py, pz);
      ctrl.target.set(CX, 0, CZ);
      ctrl.update();
    }
  }, [preset, camera, scene, size, controlsRef, onFitDistance]);

  return null;
}

// ---------------------------------------------------------------------------
// Zone focus (ADR 280): picking a zone on the floor flies the camera to frame
// THAT zone with the current preset's pose — top view stays top-down but tight
// on the zone, "Corredor" drops down the picked corridor at driver height.
// Deselecting flies back out to the whole warehouse. One-shot like
// PresetCamera: after the jump the user's own orbit/pan always wins.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// One-shot fit on load (ADR 204): sample the content bounds every ~0.5 s; when
// two consecutive samples agree (GLB + occupancy + racks all landed), frame
// the active preset and stop. If the user grabs the controls first, they win —
// the fit is cancelled, never fights an intentional camera.
// ---------------------------------------------------------------------------

function FitOnLoad({
  controlsRef,
  onFitDistance,
}: {
  controlsRef: React.RefObject<IControls | null>;
  onFitDistance: (d: number) => void;
}) {
  const { camera, scene, size } = useThree();
  const doneRef = useRef(false);
  const prevBoxRef = useRef<THREE.Box3 | null>(null);
  const frame = useRef(0);

  useEffect(() => {
    const c = controlsRef.current as unknown as
      { addEventListener?: (e: string, fn: () => void) => void;
        removeEventListener?: (e: string, fn: () => void) => void } | null;
    if (!c?.addEventListener) return;
    const cancel = () => { doneRef.current = true; };
    c.addEventListener("start", cancel);
    return () => c.removeEventListener?.("start", cancel);
  }, [controlsRef]);

  useFrame(() => {
    if (doneRef.current || ++frame.current % 30 !== 0) return;
    const ctrl = controlsRef.current;
    if (!ctrl) return;
    const box = contentBounds(scene);
    if (!box) return;
    const prev = prevBoxRef.current;
    prevBoxRef.current = box.clone();
    if (!prev || !boundsStable(prev, box, 0.5)) return;
    const preset = useUIStore.getState().cameraPreset;
    applyPose(camera, ctrl, fitPose(box, preset, size.width / size.height), onFitDistance);
    doneRef.current = true;
  });

  return null;
}

// ---------------------------------------------------------------------------
// Camera focus + follow.
//
// Two distinct behaviors, deliberately split:
//
//   1. FOLLOW   — runs every frame as long as `followAssetId` is set. Eases the
//                 orbit TARGET onto the asset and carries the camera by the same
//                 translation, so the rig tracks the truck without ever writing
//                 the camera's angle or distance. Orbit and zoom stay the user's
//                 (ADR 341); pan is disabled while following, because the target
//                 belongs to the asset and a pan would only be dragged back.
//                 Triggered by an *explicit* user action (double-click on a
//                 forklift, "Seguir" button), never by simple selection.
//
//   2. FOCUS    — one-shot: when `focusRequestId` increments, ease the orbit
//                 target toward the selected asset for ~300 ms, then stop.
//                 Crucially the lerp finishes — it does NOT keep writing to
//                 the controls target on subsequent frames, so the user's
//                 own orbit/pan input is honored as soon as the animation
//                 ends. We also abort on the controls' "start" event (user
//                 grabbed the camera), so even a 300 ms window can't fight a
//                 mouse drag.
//
// The previous implementation conflated the two and lerped every frame while
// `selectedAssetId` was truthy — that caused the "orbit snaps back on
// mouseup" bug because the user's drag was being overwritten each idle frame.
// ---------------------------------------------------------------------------

const _tmpTarget   = new THREE.Vector3();
const _followStep  = new THREE.Vector3();
const _focusStart  = new THREE.Vector3();

function CameraDirector({
  controlsRef,
}: {
  controlsRef: React.RefObject<IControls | null>;
}) {
  const followId  = useTelemetryStore((s) => s.followAssetId);
  const focusReq  = useTelemetryStore((s) => s.focusRequestId);
  const selected  = useTelemetryStore((s) => s.selectedAssetId);
  const forklifts = useTelemetryStore((s) => s.forklifts);

  // One-shot focus state — refs (not state) so updates don't trigger re-renders.
  const focusEndAtRef       = useRef<number | null>(null);
  const focusDurationRef    = useRef(300);
  const lastFocusReqRef     = useRef(focusReq);
  const focusTargetIdRef    = useRef<string | null>(null);

  // Begin a focus animation whenever focusRequestId changes (one-shot).
  useEffect(() => {
    if (lastFocusReqRef.current === focusReq) return;
    lastFocusReqRef.current = focusReq;
    if (!selected || !controlsRef.current) return;
    focusEndAtRef.current   = performance.now() + focusDurationRef.current;
    focusTargetIdRef.current = selected;
    _focusStart.copy(controlsRef.current.target);
  }, [focusReq, selected, controlsRef]);

  // While following, the orbit target IS the asset, so panning could only drag
  // it off and have it eased straight back — a fight the user always loses.
  // Rotate and zoom are untouched (ADR 341). Restored on exit so a normal
  // inspection pan still works.
  useEffect(() => {
    const ctrl = controlsRef.current;
    if (!ctrl) return;
    const c = ctrl as unknown as { enablePan?: boolean };
    if (typeof c.enablePan !== "boolean") return;
    const previous = c.enablePan;
    c.enablePan = !followId;
    return () => { c.enablePan = previous; };
  }, [followId, controlsRef]);

  // Cancel a running focus the moment the user grabs the controls — otherwise
  // the residual lerp would briefly fight their drag.
  useEffect(() => {
    const c = controlsRef.current as unknown as
      { addEventListener?: (e: string, fn: () => void) => void;
        removeEventListener?: (e: string, fn: () => void) => void } | null;
    if (!c?.addEventListener) return;
    const cancel = () => { focusEndAtRef.current = null; };
    c.addEventListener("start", cancel);
    return () => c.removeEventListener?.("start", cancel);
  }, [controlsRef]);

  useFrame(({ camera }) => {
    const ctrl = controlsRef.current;
    if (!ctrl) return;

    // (1) Continuous follow takes precedence over any one-shot focus.
    if (followId) {
      const fork = forklifts[followId];
      if (!fork) return;
      _tmpTarget.set(fork.x, 0, fork.y);
      // Move the camera by exactly the distance the target moved. Whatever
      // angle and distance the user has orbited to is preserved, because
      // neither is ever assigned. The old code lerped camera.position toward a
      // fixed (x+8, 5, y+8), which re-imposed one framing every frame and threw
      // away any orbit or zoom on the very next tick.
      _followStep.copy(ctrl.target);
      ctrl.target.lerp(_tmpTarget, 0.05);
      _followStep.subVectors(ctrl.target, _followStep);
      camera.position.add(_followStep);
      ctrl.update();
      // X-ray the walls between camera and the followed asset (ADR 103).
      xrayUniforms.uXrayOn.value = 1;
      xrayUniforms.uXrayA.value.copy(camera.position);
      xrayUniforms.uXrayB.value.set(fork.x, 0.8, fork.y);
      return;
    }
    xrayUniforms.uXrayOn.value = 0;

    // (2) One-shot focus — runs until performance.now() crosses focusEndAt.
    const endAt = focusEndAtRef.current;
    if (endAt === null) return;
    const id = focusTargetIdRef.current;
    if (!id) { focusEndAtRef.current = null; return; }
    const fork = forklifts[id];
    if (!fork) { focusEndAtRef.current = null; return; }

    const now     = performance.now();
    const elapsed = focusDurationRef.current - (endAt - now);
    const t       = Math.min(elapsed / focusDurationRef.current, 1);
    const eased   = 1 - Math.pow(1 - t, 3); // ease-out cubic

    _tmpTarget.set(fork.x, 0, fork.y);
    ctrl.target.copy(_focusStart).lerp(_tmpTarget, eased);
    ctrl.update();

    if (t >= 1) focusEndAtRef.current = null;
  });

  return null;
}

// ---------------------------------------------------------------------------
// ADR 216 — publish the view's floor footprint for the minimap's "you are here"
// rectangle. Throttled to ~30 Hz and skipped entirely while the camera is still,
// so an idle scene costs nothing and the panel's rAF loop has nothing to repaint.
// ---------------------------------------------------------------------------

const _corner = new THREE.Vector3();

function ViewFootprintReporter() {
  const { camera } = useThree();
  const lastAt = useRef(0);
  const lastPos = useRef(new THREE.Vector3(NaN, NaN, NaN));
  const lastQuat = useRef(new THREE.Quaternion(NaN, NaN, NaN, NaN));

  useEffect(() => {
    return () => clearViewRays();
  }, []);

  useFrame(() => {
    const now = performance.now();
    if (now - lastAt.current < 33) return;
    lastAt.current = now;
    if (camera.position.equals(lastPos.current) && camera.quaternion.equals(lastQuat.current)) return;
    lastPos.current.copy(camera.position);
    lastQuat.current.copy(camera.quaternion);

    const dirs = NDC_CORNERS.map(([nx, ny]) => {
      // z = 0.5: any point along the corner ray works once we subtract the eye.
      _corner.set(nx, ny, 0.5).unproject(camera).sub(camera.position).normalize();
      return [_corner.x, _corner.y, _corner.z] as [number, number, number];
    });
    publishViewRays(
      [camera.position.x, camera.position.y, camera.position.z], dirs);
  });

  return null;
}

// ---------------------------------------------------------------------------
// ADR 369 — the orbit rig's memory across a walk. While orbiting, remember the
// target every frame (the controls unmount the moment walk mode starts, so
// there is no later chance to read it); when orbiting resumes, apply the pose
// the walker left behind — once, on the first frame the new controls exist.
// ---------------------------------------------------------------------------

function OrbitMemory({ controlsRef, lastOrbit, resumeRef }: {
  controlsRef: React.RefObject<IControls | null>;
  lastOrbit: React.MutableRefObject<OrbitPose>;
  resumeRef: React.MutableRefObject<OrbitPose | null>;
}) {
  const { camera } = useThree();
  useFrame(() => {
    const ctrl = controlsRef.current;
    if (!ctrl) return;
    const resume = resumeRef.current;
    if (resume) {
      resumeRef.current = null;
      camera.position.copy(resume.position);
      ctrl.target.copy(resume.target);
      ctrl.update();
    }
    lastOrbit.current.position.copy(camera.position);
    lastOrbit.current.target.copy(ctrl.target);
  });
  return null;
}

// Dev-only extension of the ADR 199 perf hook: expose camera + controls so the
// Playwright harness can fly to a target deterministically instead of
// synthesizing drag/wheel input.
function DevProbeHook({ controlsRef }: { controlsRef: React.RefObject<IControls | null> }) {
  const { camera } = useThree();
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const w = window as unknown as { __twinCamera?: unknown; __twinControls?: unknown };
    w.__twinCamera = camera;
    w.__twinControls = controlsRef.current;
  });
  return null;
}

// ---------------------------------------------------------------------------
// Scene root
// ---------------------------------------------------------------------------

// The demo seed's 60x40 no longer sizes anything here: the heat field and the
// spaghetti take the floor's real frame from the zones API (see useFloorFrame),
// which is what ADR 143 did for zone shapes and ADR 204 for the camera.

export function Scene() {
  const preset          = useUIStore((s) => s.cameraPreset);
  const walkMode        = useUIStore((s) => s.walkMode);
  const layers          = useUIStore((s) => s.layers);
  const siteId          = useSiteId();
  const warehouseId     = useSiteStore((s) => s.activeWarehouseId);
  const controlsRef     = useRef<IControls>(null) as React.RefObject<IControls | null>;
  const clearSelection  = useTelemetryStore((s) => s.clearSelection);
  const setSelectedBin  = useUIStore((s) => s.setSelectedBin);
  const setSelectedZone = useUIStore((s) => s.setSelectedZone);
  // Zoom-out ceiling follows the fitted warehouse (ADR 204): the fixed 200 m
  // left a big site's fit distance almost AT the ceiling — no room to step back.
  const [maxDistance, setMaxDistance] = useState(200);
  const onFitDistance = (d: number) => setMaxDistance((m) => Math.max(m, Math.ceil(d * 2)));
  // ADR 369: where the orbit target was when the walk began, and the pose the
  // walk hands back. Refs, not state — neither should re-render the canvas.
  const lastOrbit       = useRef<OrbitPose>({
    position: new THREE.Vector3(...cameraPositions.iso), target: new THREE.Vector3(CX, 0, CZ),
  });
  const walkExitRef     = useRef<OrbitPose | null>(null);
  const walkEntryRef    = useRef<OrbitPose | null>(null);
  // ADR 370: the racks push back. Fetched only once a walk starts — it is the
  // warehouse's whole mask, and orbiting never needs it.
  const blockerField    = useBlockerField(siteId, warehouseId, walkMode);
  const structureTop    = useUIStore((s) => s.structureTop);
  const walkCeilingM    = structureTop && structureTop.warehouseId === warehouseId ? structureTop.topM : null;
  if (walkMode && !walkEntryRef.current) {
    walkEntryRef.current = { position: lastOrbit.current.position.clone(), target: lastOrbit.current.target.clone() };
  } else if (!walkMode) {
    walkEntryRef.current = null;
  }

  return (
    <Canvas
      // ADR 209: no shadow maps — this is an analytical inspection view, and a
      // cast shadow only hides stock on the side away from a key light.
      shadows={false}
      gl={{ antialias: true, powerPreference: "high-performance" }}
      camera={{ position: cameraPositions[preset], near: 0.1, far: 500, fov: 50 }}
      // Clicks that don't hit any 3D object (empty floor, sky) clear the
      // selection (asset, bin AND zone) and stop any active follow — this is
      // the deselect path the inspector was missing.
      onPointerMissed={() => { clearSelection(); setSelectedBin(null); setSelectedZone(null); }}
      onCreated={(state) => {
        state.scene.background = new THREE.Color(tokens.scene.bgBottom);
        state.gl.setClearColor(tokens.scene.bgBottom);
        // ADR 200: lift ACES tone mapping a stop — the default 1.0 crushed the
        // mid-tones of an already dark-navy scene (customer feedback).
        state.gl.toneMappingExposure = 1.25;
        if (import.meta.env.DEV) {
          // Perf instrumentation hook for the Playwright harness (dev only).
          const w = window as unknown as { __twinGL?: unknown; __twinScene?: unknown };
          w.__twinGL = state.gl;
          w.__twinScene = state.scene;
        }
      }}
    >
      <Suspense fallback={null}>
        <PresetCamera preset={preset} controlsRef={controlsRef} onFitDistance={onFitDistance} />
        {/* ADR 294: picking a zone does NOT move the camera. The ZoneFocus
            component that used to fly to the zone (and fly back out to the whole
            warehouse on deselect) threw away whatever the operator had framed —
            inspection is not navigation, the same rule the pallet inspector has
            always followed. `zoneFocusBox` is still stored for an explicit
            "enquadrar" button, which is a different gesture. */}
        {/* key: a warehouse/site switch re-arms the one-shot fit for the new floor. */}
        <FitOnLoad key={`fit:${siteId}:${warehouseId}`} controlsRef={controlsRef} onFitDistance={onFitDistance} />
        <CameraDirector controlsRef={controlsRef} />
        {/* ADR 216: feeds the minimap's view rectangle. */}
        <ViewFootprintReporter />
        {/* ADR 210: world-space headlight — keeps the viewed side lit as you orbit. */}
        <CameraHeadlight intensity={0.55} />
        <Warehouse />
        <AlertBeamLayer />
        <SensorLayer />
        {layers.heatmap   && <HeatmapLayer siteId={siteId} />}
        {layers.spaghetti && <SpaghettiLayer />}
        {/* key={site:warehouse}: force a fresh instance on switch rather than
            reusing one and resetting its state imperatively — see
            OccupancyLayerContainer's doc comment. Warehouse-scoped since ADR 190:
            the layer paints the ACTIVE warehouse's latest run. */}
        {/* ADR 290: the active relocation what-if's routes — sibling of the
            bins (they share the warehouse frame), see the layout-transform
            note on OccupancyLayerContainer. */}
        {warehouseId && <SimulationRoutesLayer warehouseId={warehouseId} />}
        {layers.occupancy && warehouseId && (
          <OccupancyLayerContainer key={`occ:${siteId}:${warehouseId}`} siteId={siteId} warehouseId={warehouseId} />
        )}
        {/* ADR 170 inferred-3D rack skeleton — run-independent, so it needs the
            active warehouse explicitly (occupancy resolves its warehouse via the
            run's map; this reads the structure graph directly). */}
        {layers.rackVolumes && warehouseId && (
          <RackVolumesLayerContainer key={`${siteId}:${warehouseId}`} siteId={siteId} warehouseId={warehouseId} />
        )}
        {/* ADR 193 aisle KPI badges — one container for both KPIs (they share
            the run rollup); which chips render is the layer's own concern. */}
        {(layers.kpiCoverage || layers.kpiOccupancy || layers.kpiVertical || layers.kpiAccuracy) && warehouseId && (
          <KpiBadgeLayerContainer key={`kpi:${siteId}:${warehouseId}`} siteId={siteId} warehouseId={warehouseId} />
        )}
        {/* ADR 369: the walk camera REPLACES the orbit rig — one owner of the
            camera at a time. Every orbit-side component above checks the
            controls ref and stands down while it is null. */}
        {walkMode && walkEntryRef.current ? (
          <WalkControls entry={walkEntryRef.current} exitRef={walkExitRef} field={blockerField} maxHeightM={walkCeilingM} />
        ) : (
          <OrbitControls
            ref={controlsRef as React.RefObject<any>}
            target={[CX, 0, CZ]}
            // ADR 211: no damping — this is an inspection instrument, and the
            // post-release coast made precise framing harder (user request:
            // "release the button, stop the movement").
            enableDamping={false}
            minPolarAngle={0}
            maxPolarAngle={Math.PI / 2.05}
            minDistance={5}
            maxDistance={maxDistance}
          />
        )}
        <OrbitMemory controlsRef={controlsRef} lastOrbit={lastOrbit} resumeRef={walkExitRef} />
        <AdaptiveDpr pixelated />
        {import.meta.env.DEV && <DevProbeHook controlsRef={controlsRef} />}
      </Suspense>
    </Canvas>
  );
}
